import React, { useRef, memo, ComponentPropsWithoutRef } from 'react'
import { Controller, ControllerProps, FieldValues } from 'react-hook-form'

import { useHandler } from '@surglogs-app/hooks'

import { ClearableDateInput } from './ClearableDateInput'

interface DateInputFieldProps<TValuesModel extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof ClearableDateInput>,
      'name' | 'defaultValue'
    >,
    Pick<
      ControllerProps<TValuesModel>,
      'shouldUnregister' | 'control' | 'name' | 'defaultValue'
    > {
  autoFocus?: boolean
}

/**
 * Date picker input
 *
 * Must be used with react-hook-form
 */
const Component = <TValuesModel extends FieldValues = FieldValues>({
  control,
  defaultValue,
  name,
  shouldUnregister,
  ...rest
}: DateInputFieldProps<TValuesModel>) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const onInputFocus = useHandler(() => inputRef.current?.focus())

  const render = useHandler(({ field }) => (
    <ClearableDateInput {...rest} {...field} />
  ))

  return (
    <Controller<TValuesModel>
      {...{
        defaultValue,
        control,
        name,
        onFocus: onInputFocus,
        render,
        shouldUnregister,
      }}
    />
  )
}

export const DateInputField = memo(Component) as typeof Component
